import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { PartnerManagementInjection } from "features/partnerManagement/di/PartnerManagementInjection";
import { isUndefined } from "lodash";
import { parseNoteString } from "utils/format/StringFormat";
import { areEqual, avoidDuplicate } from "utils/helpersInTs/helpersInTs";
import { B2CPartnerActionRequestEntity } from "../entities/b2cProvider/B2CPartnerActionRequestEntity";
import { B2CPartnerActionResponseEntity } from "../entities/b2cProvider/B2CPartnerActionResponseEntity";
import { B2CPartnerDetailResponseEntity } from "../entities/b2cProvider/B2CPartnerDetailResponseEntity";
import { B2CPartnerRequestEntity } from "../entities/b2cProvider/B2CPartnerRequestEntity";
import { B2CPartnerResponseEntity } from "../entities/b2cProvider/B2CPartnerResponseEntity";
import { IB2CPartnerManagementRepository } from "../repositories/IB2CPartnerManagementRepository";
import { injectable } from "tsyringe";

@injectable()
export class B2CPartnerManagementUseCase {
  private b2cPartnerRepo: IB2CPartnerManagementRepository;

  constructor() {
    this.b2cPartnerRepo = PartnerManagementInjection.getB2CRepo();
  }

  private adjustBody(
    body: B2CPartnerActionRequestEntity,
    detailInfo: B2CPartnerDetailResponseEntity,
  ) {
    let renderedBody = Object.keys(body)?.filter(
      (item) =>
        !Array.isArray(body[item]) &&
        item !== "configuration" &&
        item !== "code" &&
        item !== "ipConfig" &&
        item !== "isChain" &&
        !isUndefined(
          avoidDuplicate(
            typeof detailInfo[item] === "object"
              ? detailInfo[item]?.["code"]?.toString()
              : detailInfo[item]?.toString(),
            body[item]?.toString(),
          ),
        ),
    );
    let afterBody = {
      code: body?.code,
      ...renderedBody?.reduce((a, v) => ({ ...a, [v]: body[v] }), {}),
      isChain: Boolean(
        parseNoteString(detailInfo?.note)?.["isChain"] ===
        body?.isChain?.toString(),
      )
        ? undefined
        : body?.isChain,
      ipConfig: isUndefined(body?.ipConfig)
        ? undefined
        : areEqual(detailInfo?.ipConfig || [], body?.ipConfig)
          ? undefined
          : body?.ipConfig,
    } as typeof body;
    return afterBody;
  }

  async getList(
    params: B2CPartnerRequestEntity,
  ): Promise<ListBaseApiResponseModel<B2CPartnerResponseEntity>> {
    return await this.b2cPartnerRepo.getList(params);
  }

  async getB2BList(
    params: B2CPartnerRequestEntity,
  ): Promise<ListBaseApiResponseModel<B2CPartnerResponseEntity>> {
    return await this.b2cPartnerRepo.getB2BList(params);
  }

  async getDetail(params: {
    code: string;
  }): Promise<BaseApiResponseModel<B2CPartnerDetailResponseEntity>> {
    return await this.b2cPartnerRepo.getDetail(params);
  }

  async createPartner(
    body: B2CPartnerActionRequestEntity,
    detailInfo: B2CPartnerDetailResponseEntity,
  ): Promise<BaseApiResponseModel<B2CPartnerActionResponseEntity>> {
    const updatedBody = this.adjustBody(body, detailInfo);
    return await this.b2cPartnerRepo.createPartner(updatedBody);
  }

  async updatePartner(
    body: B2CPartnerActionRequestEntity,
    detailInfo: B2CPartnerDetailResponseEntity,
  ): Promise<BaseApiResponseModel<B2CPartnerDetailResponseEntity>> {
    const updatedBody = this.adjustBody(body, detailInfo);
    return await this.b2cPartnerRepo.updatePartner(updatedBody);
  }

  async deletePartner(params: {
    code: string;
  }): Promise<BaseApiResponseModel<B2CPartnerResponseEntity>> {
    return await this.b2cPartnerRepo.deletePartner(params);
  }
}
